import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { ApiService, CommitmentService } from "../../../../../../api";
import { translate } from "../../../../../../common/providers";
import { InfiniteList2 } from "../../../../../../common/components";
import styles from "./HistoryView.module.css";
import HistoryRow from "../history-row/HistoryRow";
import { HISTORY_ITEM_TYPE, HISTORY_EVENT_TYPE } from "../../constants/constants";

const DEFAULT_LIMIT = 25;

const mapFieldNameToType = (fieldName) => {
	const fieldNameLower = (fieldName || "").toLowerCase();
	switch (fieldNameLower) {
		case "details":
			return HISTORY_ITEM_TYPE.DETAILS;
		case "pilot":
			return HISTORY_ITEM_TYPE.PILOT;
		case "theme":
			return HISTORY_ITEM_TYPE.THEME;
		case "deadline":
		case "completion deadline":
			return HISTORY_ITEM_TYPE.DEADLINE;
		case "risk":
			return HISTORY_ITEM_TYPE.RISK;
		case "frequency":
			return HISTORY_ITEM_TYPE.FREQUENCY;
		case "priority":
			return HISTORY_ITEM_TYPE.PRIORITY;
		case "status":
			return HISTORY_ITEM_TYPE.STATUS;
		case "comment":
			return HISTORY_ITEM_TYPE.COMMENT;
		default:
			return HISTORY_ITEM_TYPE.FIELD_EDITED;
	}
};

const mapEventType = (eventType, fieldName) => {
	switch (eventType) {
		case HISTORY_EVENT_TYPE.COMMITMENT_CREATED:
			return HISTORY_ITEM_TYPE.CREATED;
		case HISTORY_EVENT_TYPE.COMMITMENT_SOURCE_ADDED:
			return HISTORY_ITEM_TYPE.CONTENT_ADDED;
		case HISTORY_EVENT_TYPE.COMMITMENT_SOURCE_REMOVED:
			return HISTORY_ITEM_TYPE.CONTENT_REMOVED;
		case HISTORY_EVENT_TYPE.COMMITMENT_COMMENT_ADDED:
			return HISTORY_ITEM_TYPE.COMMENT;
		case HISTORY_EVENT_TYPE.COMMITMENT_COMMENT_MODIFIED:
			return HISTORY_ITEM_TYPE.COMMENT_MODIFIED;
		case HISTORY_EVENT_TYPE.COMMITMENT_COMMENT_REMOVED:
			return HISTORY_ITEM_TYPE.COMMENT_REMOVED;
		case HISTORY_EVENT_TYPE.FIELD_EDITED:
		default:
			return mapFieldNameToType(fieldName);
	}
};

const HistoryView = ({ commitmentId }) => {
	const [infiniteRequest, setInfiniteRequest] = useState(null);
	const cancelTokenSourceRef = useRef(null);

	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();

		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);

	useEffect(() => {
		if (!commitmentId) {
			setInfiniteRequest(null);
			return;
		}
		setInfiniteRequest(
			() =>
				({ page, limit }) =>
					CommitmentService.getHistory(
						{ commitmentId },
						{ page, limit: limit || DEFAULT_LIMIT },
						cancelTokenSourceRef.current?.token
					).then((response) => {
						const contents = response?.data?.contents || response?.contents || [];
						const transformedContents = contents.map((item, index) => ({
							id: `${item.commitmentId}-${item.eventType}-${index}`,
							username: item.displayName || item.userEmail || "",
							companyName: "",
							date: new Date().toISOString(),
							elapsed: item.elapsed || { value: 0, unit: "SECONDS" },
							eventType: item.eventType,
							fieldChanged: mapEventType(item.eventType, item.fieldName),
							fieldName: item.fieldName,
							actionTaken: item.eventType === HISTORY_EVENT_TYPE.COMMITMENT_CREATED ? "CREATE" : "MODIFY",
							oldContent: "",
							newContent: item.fieldValue || "",
							contentId: item.contentId || null,
							comment: item.comment || null,
						}));
						return {
							...response?.data,
							data: transformedContents,
							contents: transformedContents,
						};
					})
		);
	}, [commitmentId]);

	const rowRenderer = (row) => <HistoryRow key={row.id} history={row} />;

	if (!commitmentId) {
		return (
			<div className={styles.container}>
				<div className={styles.emptyState}>
					{translate("commitment-tracking.empty-state.no-commitment-selected")}
				</div>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<InfiniteList2
				className={styles.listContainer}
				request={infiniteRequest}
				rowRenderer={rowRenderer}
				scrollContainerClassName={styles.listScrollContainer}
			/>
		</div>
	);
};

HistoryView.propTypes = {
	commitmentId: PropTypes.number,
};

HistoryView.defaultProps = {
	commitmentId: null,
};

export default HistoryView;
